import { Alert, Button, buttonClass, cardClass, fieldClass, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useApi, usePoll } from '../hooks';
import { Empty, ErrorBanner, Loading, Time, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { inboxItem } from './Approvals';
import { AddWebsite } from './Admin';

// Editing the workspace's website (docs/plans/website-editing-plan.md): the
// site repositories in Settings, asking the website agent for a change on its
// page, and a draft seen as its preview before it is signed. Signing opens a
// pull request; nothing is pushed to the live site.

interface SiteRepo {
  name: string; repo: string; branch: string | null; allowed_paths: string[]; site_url: string | null; created_at: string; rotated_at: string | null;
  search_console: { property: string; health: string; read_through: string | null } | null;
  bing: { site: string; health: string; read_through: string | null } | null;
}

// Settings, Connectors: the repositories and Connect GitHub.
// Patterns: data-display/table.
export function SiteRepositories() {
  const repos = useApi<SiteRepo[]>('/v1/connectors/github');
  const toast = useToast();
  const connected = useSearchParams()[0].get('connected') === 'github';
  const [revoking, setRevoking] = useState<SiteRepo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function revoke() {
    if (!revoking) return;
    setBusy(true); setError(null);
    try { await api(`/v1/connectors/github/${encodeURIComponent(revoking.name)}/revoke`, { method: 'POST', body: {} }); toast(`${revoking.name} revoked`); setRevoking(null); void repos.reload(); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <>
      <h3 id="repos-h" style={{ margin: '8px 0 0' }}>Site repositories</h3>
      <p className="small muted" style={{ margin: 0 }}>The GitHub repository your website is built from. The website agent reads it and drafts changes; each one you sign becomes a pull request you merge on GitHub. Nothing is pushed to your live site.</p>
      <ErrorBanner error={repos.error} />
      {!repos.data ? <Loading label="Loading site repositories" /> : !repos.data.length ? <p className="small faint" style={{ margin: 0 }}>No site repositories yet.</p> : (
        <Table label="Site repositories">
          <caption className="sr-only">Site repositories</caption>
          <thead><tr><th scope="col">Name</th><th scope="col">Repository</th><th scope="col">Agents may change</th><th scope="col">Connected</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>{repos.data.map((r) => (
            <tr key={r.name}>
              <td>{r.name}</td><td><code>{r.repo}</code>{r.branch && <div className="small faint">Pull requests go to {r.branch}</div>}</td>
              <td className="small">{r.allowed_paths.map((p) => <div key={p}><code>{p}</code></div>)}</td>
              <td className="small"><Time iso={r.rotated_at ?? r.created_at} /></td>
              <td><Button type="button" variant="ghost" size="sm" onClick={() => { setError(null); setRevoking(r); }}>Revoke {r.name}</Button></td>
            </tr>
          ))}</tbody>
        </Table>
      )}
      {connected && <Alert tone="success" role="status">GitHub is connected.</Alert>}
      <ConnectGithub returnTo="/admin/connections" />
      <ConfirmDialog open={!!revoking} title={`Revoke ${revoking?.name ?? ''}?`} confirmLabel="Revoke repository" tone="danger" busy={busy} onConfirm={revoke} onCancel={() => setRevoking(null)}>
        <p style={{ margin: 0 }}>The website agent can no longer read it or open pull requests on it. The record stays; nothing is deleted.</p>
        <ErrorBanner error={error} />
      </ConfirmDialog>
    </>
  );
}

// Connect GitHub: off to GitHub to install the deployment's GitHub App on the
// repositories the person picks, and back to returnTo. Without an app, nothing
// connects until the operator sets one up.
const CONNECT_ERRORS: Record<string, string> = {
  expired: 'The connection took too long. Connect again.',
  not_your_installation: 'GitHub says that installation is not yours. Connect again with the account that installed it.',
  not_installed: 'The app was not installed. Connect again and pick your site\'s repository.',
  access_denied: 'You cancelled on GitHub. Nothing was connected.',
};
function ConnectGithub({ returnTo }: { returnTo: string }) {
  const app = useApi<{ configured: boolean }>('/v1/connectors/github/app');
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const failed = params.get('connect_error');
  async function connect() {
    setBusy(true); setError(null);
    try { window.location.assign((await api<{ url: string }>('/v1/connectors/github/install', { body: { return_to: returnTo } })).url); } catch (e) { setError(e); setBusy(false); }
  }
  return (
    <div className="stack" style={{ gap: 8 }}>
      {failed && <Alert tone="warning" role="status">{CONNECT_ERRORS[failed] ?? 'GitHub did not finish connecting. Connect again.'}</Alert>}
      <ErrorBanner error={app.error ?? error} />
      {app.data?.configured
        ? <div className="row"><Button type="button" disabled={busy} onClick={() => void connect()}>{busy ? 'Opening GitHub…' : 'Connect GitHub'}</Button>
            <span className="small muted">GitHub asks which repositories to share. Pick the one your site is built from.</span></div>
        : app.data && <p className="small muted" style={{ margin: 0 }}>Mazu has no GitHub App set up here yet, so your site cannot be connected. Ask the operator to set one up.</p>}
    </div>
  );
}

// Setup on hire (docs/plans/website-editing-plan.md): what this agent needs
// before it can work, asked on its page. Where the site is hosted first, then
// the connection, then what is connected and the paths it may change.
// Patterns: forms/radio for the question, inside a card.
export function AgentSetup({ agentId, agentName, needs, admin, onChanged }: {
  agentId: string; agentName: string; needs: Array<{ connector: string; why: string; met: boolean }>; admin: boolean; onChanged: () => void;
}) {
  const [params] = useSearchParams();
  const [host, setHost] = useState<'github' | 'other' | null>(null);
  const github = needs.find((n) => n.connector === 'github');
  const web = needs.find((n) => n.connector === 'web');
  // A website for a browser agent: the same form as Settings, asked here; once added, Give it a job takes over.
  if (web) return web.met ? null : (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="setup-h">
      <div>
        <h2 id="setup-h">Set up {agentName}</h2>
        <div className="faint">{web.why}</div>
      </div>
      {admin ? <AddWebsite onAdded={onChanged} /> : <p className="small muted" style={{ margin: 0 }}>No website is added yet. An admin adds it here.</p>}
    </section>
  );
  if (!github) return null;
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="setup-h">
      <div>
        <h2 id="setup-h">Set up {agentName}</h2>
        <div className="faint">{github.why}</div>
      </div>
      {params.get('connected') === 'github' && <Alert tone="success" role="status">GitHub is connected.</Alert>}
      {github.met ? <ConnectedSites /> : !admin ? (
        <p className="small muted" style={{ margin: 0 }}>Your website is not connected yet. An admin connects it here.</p>
      ) : <>
        <fieldset className="field">
          <legend>Where is your site hosted?</legend>
          <div className="choice" role="radiogroup">
            <label><input type="radio" name="site-host" checked={host === 'github'} onChange={() => setHost('github')} />GitHub</label>
            <label><input type="radio" name="site-host" checked={host === 'other'} onChange={() => setHost('other')} />Somewhere else</label>
          </div>
        </fieldset>
        {host === 'github' && <ConnectGithub returnTo={`/agents/${encodeURIComponent(agentId)}`} />}
        {host === 'other' && <p className="small muted" style={{ margin: 0 }}>Only GitHub works for now. Tell Mazu where your site is hosted, so it can be added.</p>}
      </>}
    </section>
  );
}

// The connected site repositories and the paths an agent may change in each, changed in place.
function ConnectedSites() {
  const repos = useApi<SiteRepo[]>('/v1/connectors/github');
  return !repos.data ? <Loading label="Loading site repositories" /> : (
    <ul className="stack" style={{ margin: 0, padding: 0, listStyle: 'none', gap: 8 }}>
      {repos.data.map((r) => <li key={r.name} className="stack" style={{ gap: 10 }}><SitePaths repo={r} onSaved={() => void repos.reload()} /><SearchConsole repo={r} onChanged={() => void repos.reload()} /><BingKey repo={r} onChanged={() => void repos.reload()} /></li>)}
    </ul>
  );
}

function SitePaths({ repo, onSaved }: { repo: SiteRepo; onSaved: () => void }) {
  const toast = useToast();
  const [paths, setPaths] = useState(repo.allowed_paths.join(', '));
  const [siteUrl, setSiteUrl] = useState(repo.site_url ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await api(`/v1/connectors/github/${encodeURIComponent(repo.name)}/paths`, { method: 'PUT', body: { allowed_paths: paths.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean), site_url: siteUrl.trim() || null } });
      toast(`${repo.name}: saved`); onSaved();
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <form className="stack" style={{ gap: 6 }} onSubmit={save}>
      <div><strong>{repo.name}</strong> <code className="small">{repo.repo}</code></div>
      <ErrorBanner error={error} />
      <Field label={`Paths agents may change in ${repo.name}`} hint="Comma-separated. ** matches anything below a folder, * anything within one.">
        <input className={fieldClass} autoComplete="off" value={paths} onChange={(e) => setPaths(e.target.value)} />
      </Field>
      <Field label={`Where ${repo.name} is live`} hint="The address visitors use, like https://example.com. Once you merge a change, Mazu checks it reached the pages there.">
        <input className={fieldClass} type="url" inputMode="url" autoComplete="url" placeholder="https://" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} />
      </Field>
      <div><Button type="submit" variant="secondary" size="sm" disabled={busy || !paths.trim()}>{busy ? 'Saving…' : 'Save'}</Button></div>
    </form>
  );
}

// A site's Search Console: off to Google to give
// read-only access, and back here. Once the site is live somewhere; its numbers come from the next daily read.
const SEARCH_ERRORS: Record<string, string> = {
  expired: 'The connection took too long. Connect again.',
  access_denied: 'You cancelled on Google. Nothing was connected.',
  no_property: 'That Google account has no Search Console property for this site. Connect again with the account that can see it in Search Console.',
  no_site_url: 'Save where the site is live first, then connect Search Console.',
  google_refused: 'Google did not finish connecting. Connect again.',
};
const day = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
function SearchConsole({ repo, onChanged }: { repo: SiteRepo; onChanged: () => void }) {
  const [params] = useSearchParams();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const sc = repo.search_console;
  // Google's answer comes back naming the site it was for, so it shows only under that site.
  const mine = params.get('site') === repo.name;
  const failed = mine ? params.get('connect_error') : null;
  async function connect() {
    setBusy(true); setError(null);
    try {
      window.location.assign((await api<{ url: string }>(`/v1/connectors/github/${encodeURIComponent(repo.name)}/search-console`, { body: { return_to: `${window.location.pathname}?site=${encodeURIComponent(repo.name)}` } })).url);
    } catch (e) { setError(e); setBusy(false); }
  }
  async function disconnect() {
    setBusy(true); setError(null);
    try { await api(`/v1/connectors/search_console/${encodeURIComponent(repo.name)}/revoke`, { method: 'POST', body: {} }); toast(`${repo.name}: Search Console disconnected`); setConfirm(false); onChanged(); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  if (!repo.site_url && !sc) return null;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div><strong>Search Console</strong></div>
      {mine && params.get('connected') === 'search_console' && sc && <Alert tone="success" role="status">Search Console is connected.</Alert>}
      {failed && SEARCH_ERRORS[failed] && !sc && <Alert tone="warning" role="status">{SEARCH_ERRORS[failed]}</Alert>}
      <ErrorBanner error={error} />
      {sc ? <>
        <p className="small" style={{ margin: 0 }}>
          Reading <code>{sc.property}</code>. {sc.read_through ? <>Clicks and impressions for each page are in up to {day(sc.read_through)}.</> : 'The first numbers arrive after the next daily read.'}
        </p>
        {sc.health === 'needs_sign_in' && <Alert tone="warning" role="status">Google stopped accepting this sign-in, so nothing new is read. Connect again.</Alert>}
        <div className="row">
          <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void connect()}>Connect again</Button>
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => { setError(null); setConfirm(true); }}>Disconnect Search Console</Button>
        </div>
      </> : <div className="row">
        <Button type="button" variant="secondary" disabled={busy} onClick={() => void connect()}>{busy ? 'Opening Google…' : 'Connect Search Console'}</Button>
        <span className="small muted">Sign in with a Google account that can see {repo.site_url} in Search Console. Mazu only reads it: clicks, impressions, and position for each page, so the website agent sees what its changes did in search.</span>
      </div>}
      <ConfirmDialog open={confirm} title="Disconnect Search Console?" confirmLabel="Disconnect" tone="danger" busy={busy} onConfirm={disconnect} onCancel={() => setConfirm(false)}>
        <p style={{ margin: 0 }}>Mazu stops reading {repo.name}'s search numbers. What it already read stays.</p>
        <ErrorBanner error={error} />
      </ConfirmDialog>
    </div>
  );
}

// A site's Bing Webmaster Tools: Bing's index feeds Copilot and DuckDuckGo. An admin pastes the API key;
// it is kept only when Bing verified the site for it. Pattern: forms/text-field.
function BingKey({ repo, onChanged }: { repo: SiteRepo; onChanged: () => void }) {
  const toast = useToast();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const b = repo.bing;
  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try { await api(`/v1/connectors/github/${encodeURIComponent(repo.name)}/bing`, { method: 'PUT', body: { api_key: key.trim() } }); setKey(''); toast(`${repo.name}: Bing connected`); onChanged(); } catch (err) { setError(err); } finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true); setError(null);
    try { await api(`/v1/connectors/bing/${encodeURIComponent(repo.name)}/revoke`, { method: 'POST', body: {} }); toast(`${repo.name}: Bing disconnected`); setConfirm(false); onChanged(); } catch (err) { setError(err); } finally { setBusy(false); }
  }
  if (!repo.site_url && !b) return null;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div><strong>Bing Webmaster Tools</strong></div>
      {b && <p className="small" style={{ margin: 0 }}>Reading <code>{b.site}</code>. {b.read_through ? <>Bing's weekly numbers are in up to {day(b.read_through)}.</> : 'The first numbers arrive after the next daily read.'}</p>}
      {b?.health === 'needs_sign_in' && <Alert tone="warning" role="status">Bing refused this key, so nothing new is read. Paste a new one.</Alert>}
      <form className="stack" style={{ gap: 6 }} onSubmit={save}>
        <ErrorBanner error={error} />
        <Field label={b ? `New Bing API key for ${repo.name}` : `Bing API key for ${repo.name}`} hint="In Bing Webmaster Tools: Settings, API access. Add and verify the site there first; Bing can import it from Search Console. Mazu only reads it.">
          <input className={fieldClass} autoComplete="off" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)} />
        </Field>
        <div className="row">
          <Button type="submit" variant="secondary" size="sm" disabled={busy || key.trim().length < 16}>{busy ? 'Checking with Bing…' : b ? 'Replace key' : 'Connect Bing'}</Button>
          {b && <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => { setError(null); setConfirm(true); }}>Disconnect Bing</Button>}
        </div>
      </form>
      <ConfirmDialog open={confirm} title="Disconnect Bing?" confirmLabel="Disconnect" tone="danger" busy={busy} onConfirm={disconnect} onCancel={() => setConfirm(false)}>
        <p style={{ margin: 0 }}>Mazu stops reading {repo.name}'s Bing numbers. What it already read stays.</p>
        <ErrorBanner error={error} />
      </ConfirmDialog>
    </div>
  );
}

// The website agent's page: ask for a change in plain words. Pattern: forms/textarea.
interface Dispatched { outcome: 'queued' | 'duplicate' | 'asked' | 'unmatched'; exception_id?: string }
export function AskForSiteChange({ agentName }: { agentName: string }) {
  const repos = useApi<SiteRepo[]>('/v1/connectors/github');
  const [connector, setConnector] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<Dispatched | null>(null);
  const list = repos.data ?? [];
  const chosen = connector || list[0]?.name || '';
  async function ask(e: FormEvent) {
    e.preventDefault(); setError(null); setDone(null); setBusy(true);
    try {
      const out = await api<Dispatched>('/v1/work', { body: { class: 'site_request', subject: { connector: chosen, message: message.trim() }, title: message.trim().slice(0, 80) } });
      setDone(out);
      if (out.outcome === 'queued' || out.outcome === 'duplicate') setMessage('');
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="site-ask-h">
      <div><h2 id="site-ask-h">Ask for a change to your website</h2><div className="faint">Say what to add or change. {agentName} drafts it file by file; you read it beside the current page and sign it, and it becomes a pull request you merge.</div></div>
      <ErrorBanner error={repos.error} />
      {!repos.data ? <Loading label="Loading site repositories" /> : !list.length ? (
        <Empty title="No site repository yet" action={<Link className={buttonClass({ variant: 'secondary' })} to="/admin/connections">Connect one in Settings</Link>}>
          <p style={{ margin: '4px 0 0' }}>Connect the GitHub repository your website is built from first.</p>
        </Empty>
      ) : (
        <form className="form-grid" onSubmit={ask} aria-labelledby="site-ask-h">
          <div className="full"><ErrorBanner error={error} focusOnShow /></div>
          {list.length > 1 && <Field label="Website"><select className={selectClass} value={chosen} onChange={(e) => setConnector(e.target.value)}>{list.map((r) => <option key={r.name} value={r.name}>{r.name} ({r.repo})</option>)}</select></Field>}
          <Field label="What should change?" hint="As you would tell a person: which page, and what it should say." required className="full">
            <textarea className={textareaClass} rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
          <div className="full row"><Button type="submit" disabled={busy || message.trim().length < 5}>{busy ? 'Asking…' : 'Ask for the change'}</Button></div>
        </form>
      )}
      {done && (done.outcome === 'queued' || done.outcome === 'duplicate'
        ? <Alert tone="success" role="status">{agentName} is drafting it. The draft waits for you under Needs you. <Link to="/desk/needs-you">See what needs you</Link></Alert>
        : <Alert tone="warning" role="status">No website agent is working yet, so the request waits under Needs you. {done.exception_id && <Link to={inboxItem(`exception:${done.exception_id}`)}>Open it</Link>}</Alert>)}
    </section>
  );
}

// A site.change under Needs you: what it changes and why, and the draft as the site will show it.
// The code is not shown (ruled 2026-10-27): the signer judges the page, not the files.
export function SiteChangeBody({ proposalId, payload, computed }: { proposalId: string; payload: { connector: string; title: string; why: string; base_commit: string; files: Array<{ path: string; content: string }>; live_check?: Array<{ url: string; text: string }> }; computed: any }) {
  const repo = computed?.site_change?.repo as string | undefined;
  const checks = payload.live_check ?? [];
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="small"><strong>{payload.title}</strong>{repo && <> · <code>{repo}</code></>}</div>
      <div className="note">{payload.why}</div>
      <SitePreview proposalId={proposalId} />
      {checks.length > 0 && (
        <div className="small">
          <strong>Checked on your site once it is merged</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{checks.map((c) => <li key={c.url}><a href={c.url} target="_blank" rel="noreferrer">{c.url}</a> shows "{c.text}"</li>)}</ul>
        </div>
      )}
      <p className="small muted" style={{ margin: 0 }}>Approving opens a pull request on its own branch. Your live site changes only when you merge it on GitHub, and Mazu tells you if the change does not reach it.</p>
    </div>
  );
}

// The draft built with the site's own build (docs/plans/website-access-and-hosting-plan.md, Part 4):
// each page it names in a frame, beside a link to the same page live. Patterns: data-display/comparison-table,
// ai-intelligence/ai-loading-states while it builds. The frame runs the site's scripts in an opaque origin.
interface Preview { state: 'off' | 'closed' | 'waiting' | 'building' | 'ready' | 'failed'; url: string | null; pages: Array<{ live: string; preview: string }>; error: string | null; log: string | null }
function SitePreview({ proposalId }: { proposalId: string }) {
  const p = useApi<Preview>(`/v1/site-previews/${proposalId}`, [proposalId]);
  const building = p.data?.state === 'waiting' || p.data?.state === 'building';
  usePoll(building, () => void p.reload());
  const [at, setAt] = useState(0);
  const d = p.data;
  if (!d || d.state === 'off' || d.state === 'closed') return null;
  const pages = d.pages.length ? d.pages : d.url ? [{ live: '', preview: d.url }] : [];
  const page = pages[Math.min(at, pages.length - 1)];
  return (
    <section className="stack" style={{ gap: 6 }} aria-labelledby={`preview-${proposalId}`}>
      <strong id={`preview-${proposalId}`} className="small">Preview on your site</strong>
      {building && <Loading label="Building the draft with your site's own build. This takes about a minute." />}
      {d.state === 'failed' && (
        <Alert tone="warning" role="status">
          The preview did not build: {d.error}
          {d.log && <details><summary>The end of the build's log</summary><pre className="small" style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>{d.log}</pre></details>}
        </Alert>
      )}
      {d.state === 'ready' && page && <>
        {pages.length > 1 && (
          <Field label="Page"><select className={selectClass} value={at} onChange={(e) => setAt(Number(e.target.value))}>{pages.map((x, i) => <option key={x.preview} value={i}>{new URL(x.preview).pathname}</option>)}</select></Field>
        )}
        <iframe title={`Preview of ${new URL(page.preview).pathname}`} src={page.preview} sandbox="allow-scripts" style={{ width: '100%', height: 480, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }} />
        <div className="row small" style={{ gap: 12 }}>
          <a href={page.preview} target="_blank" rel="noreferrer">Open the preview</a>
          {page.live && <a href={page.live} target="_blank" rel="noreferrer">Open the live page</a>}
          <span className="muted">Private, not indexed, gone once you decide.</span>
        </div>
      </>}
    </section>
  );
}
